import React, { FormEvent, useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useAuth0 } from '@auth0/auth0-react';
import _ from 'lodash';
import {
 IComboBoxOption, Icon, Stack, TextField,
} from '@fluentui/react';
import {
  default_page_size,
  stackTokens15,
  textFieldStyles300,
} from '../../utils';
import {
  GET_CUSTOMER_CONTRACT_UPDATES,
  ICustomerContractUpdate,
} from '../../utils/CustomerContractUpdate';
import useDebounce from '../../components/hooks/useDebounce';
import { ComboboxWithFilter } from '../../components/parts';
import { useAppDispatch } from '../../redux/hooks';
import { SeverityLevel, throwError } from '../../redux/error/errorSlice';
import { toastError } from '../../utils/toast';
import { GET_TAX_CODES } from '../../utils/TaxCode';
import PageHeader from '../../components/parts/PageHeader';
import CustomerContractUpdatesOverview from './components/CustomerContractUpdatesOverview';

interface IPagedCustomerContractUpdate {
  customerContractUpdates: ICustomerContractUpdate[];
  skip: number;
  take: number;
  filter: string;
  initialLoad: boolean;
  showLoadMore: boolean;
  lazyLoading: boolean;
  isFiltered: boolean;
  isSortedAsc: boolean;
  sortedField: string;
}

const CustomerContractUpdates = () => {
  const { isAuthenticated } = useAuth0();
  const [pagedState, setPagedState] = useState<IPagedCustomerContractUpdate>({
    customerContractUpdates: [],
    skip: 0,
    take: default_page_size,
    filter: '',
    showLoadMore: true,
    lazyLoading: true,
    isFiltered: false,
    initialLoad: true,
    isSortedAsc: false,
    sortedField: 'document_date',
  });

  const [filter, setFilter] = useState('');
  const [taxCodes, setTaxCodes] = useState([]);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined,
  );

  const debouncedValue = useDebounce(filter, 500);

  useEffect(() => {
    setPagedState(prevState => ({
      ...prevState,
      customerContractUpdates: [],
      skip: 0,
    }));
  }, [filter]);

  const getOrderBy = () => {
    const orderByObject: any = {}; // deliberately kept any as type to index by string
    orderByObject[pagedState.sortedField] = pagedState.isSortedAsc
      ? 'asc'
      : 'desc';
    return orderByObject;
  };

  const getFilters = (filterString: string, status?: string) => {
    const filterArray = filterString.split(' ');

    const filterObject: any = {
      AND: [{ status: { equals: status } }],
    };
    for (let i = 0; i < filterArray.length; i++) {
      const filterValue: any = {
        OR: [
          { document_no: { contains: filterArray[i] } },
          { summary: { contains: filterArray[i] } },
          {
            construction_site: {
              name: { contains: filterArray[i] },
            },
          },
        ],
      };

      filterObject.AND.push(filterValue);
    }

    return filterObject;
  };

  useQuery(GET_TAX_CODES, {
    fetchPolicy: 'no-cache',
    onCompleted: (x: any) => {
      setTaxCodes(x.findManyTaxCodes);
    },
  });

  const {
    data: dataCustomerContractUpdates,
    loading,
    error,
    fetchMore,
    refetch,
  } = useQuery(GET_CUSTOMER_CONTRACT_UPDATES, {
    // Geen client.cache.reset() meer bij zoeken/sorteren: elke filter + sortering heeft een eigen
    // cache-ingang (skipTakePagination in App.tsx). Eerst tonen wat in de cache zit en altijd
    // verversen bij de server; daarna cache-first, zodat "meer laden" geen extra verversing uitlokt.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    // notifyOnNetworkStatusChange: true,
    variables: {
      filter: getFilters(debouncedValue, statusFilter),
      take: pagedState.take,
      skip: 0,
      orderBy: getOrderBy(),
    },
    onError: error => {
      toastError(error.message);
    },
    // afetchPolicy: 'no-cache',
  });

  const onSearchValueChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string | undefined,
  ) => {
    const filterString = newValue ? newValue.toLowerCase() : '';
    setFilter(filterString);
  };

  const setSorting = (isSortedAsc: boolean, sortedField: string) => {
    clearCustomerContractUpdates();
    setPagedState(prevState => ({
      ...prevState,
      isSortedAsc,
      sortedField,
    }));
  };

  const clearCustomerContractUpdates = () => {
    setPagedState(prevState => ({
      ...prevState,
      customerContractUpdates: [],
      skip: 0,
    }));
  };

  const fetchMoreCustomerContractUpdates = () => {
    // verder vanaf wat er getoond wordt (zoals bij leads/klanten)
    const currentCount = dataCustomerContractUpdates?.findManyCustomerContractUpdates?.length || 0;
    // Load more quard

    fetchMore({
      variables: {
        filter: getFilters(debouncedValue, statusFilter), // inUse == active?
        take: pagedState.take,
        skip: currentCount,
        orderBy: getOrderBy(),
      },
    });

    setPagedState(prevState => ({
      ...prevState,
      skip: currentCount,
    }));
  };

  if (!isAuthenticated) return <p>Verboden</p>;
  if (error) return <p>Oeps, er ging iets mis...</p>;

  return (
    <Stack tokens={stackTokens15}>
      <div style={{ padding: '25px' }}>
        <PageHeader
          title="Meerwerkbonnen"
          iconName="ServerEnviroment"
          loading={loading}
        />

        <div style={{ display: 'flex' }}>
          <TextField
            label='Zoeken...'
            value={filter}
            styles={textFieldStyles300}
            onChange={onSearchValueChange}
            style={{ marginRight: 30 }}
          />

          <div style={{ width: 300, marginLeft: 20 }}>
            <ComboboxWithFilter
              label='Status'
              options={[
              { key: 'all', text: 'Alle' },
              { key: 'NEW', text: 'Nieuw' },
              { key: 'CANCELLED', text: 'Geannuleerd' },
              { key: 'INVOICED', text: 'Op factuur' },
              { key: 'COMPLETED', text: 'Afgesloten' },
            ]}
              value={statusFilter}
              multiline={false}
              callBack={(newValue: IComboBoxOption[]) => {
              setPagedState(prevState => ({
                ...prevState,
                customerContractUpdates: [],
              }));
              if (
                newValue &&
                newValue.length > 0 &&
                newValue[0].key !== 'all'
              ) {
                setStatusFilter(newValue[0].key as string);
              } else {
                setStatusFilter(undefined);
              }
            }}
            />
          </div>
        </div>
      </div>

      <CustomerContractUpdatesOverview
        refetch={refetch}
        customerContractUpdates={
          dataCustomerContractUpdates
            ? dataCustomerContractUpdates.findManyCustomerContractUpdates
            : []
        }
        loading={loading}
        setSorting={setSorting}
        isSortedAsc={pagedState.isSortedAsc}
        sortedField={pagedState.sortedField}
        showLoadMore={pagedState.showLoadMore}
        loadMore
        loadMoreCallback={fetchMoreCustomerContractUpdates}
        lazyLoading={pagedState.lazyLoading}
        taxCodes={taxCodes}
      />
    </Stack>
  );
};

export default CustomerContractUpdates;
